import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  LabelList,
  type LabelProps,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { axisLabel, axisMargin, formatValue, legendStyle } from './chart-utils';
import { CategoryTick, categoryAxis, type CategoryTickProps } from './axis-ticks';

/**
 * Beschriftung mit der Stapel-Summe über dem obersten Balkensegment. Recharts
 * reicht pro Datenpunkt `x`/`y`/`width`/`index` (als `LabelProps`) herein; die
 * Summe aller Serien holen wir uns über den Index aus den Rohdaten. Styling wie
 * die Achsentitel (12px, gedämpfte Brand-Farbe), damit sich die Summe einreiht.
 */
function makeStackTotalLabel(
  data: Record<string, unknown>[],
  dataKeys: string[],
  unit?: string,
  decimals?: number,
) {
  return function StackTotalLabel(props: LabelProps) {
    const { x, y, width, index } = props;
    if (index == null) return null;
    const px = Number(x);
    const py = Number(y);
    const pw = Number(width);
    const row = data[index];
    if (!row || !Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pw)) {
      return null;
    }
    const sum = dataKeys.reduce((acc, key) => {
      const n = Number(row[key]);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
    return (
      <text
        x={px + pw / 2}
        y={py - 6}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="var(--color-brand-muted, #6b7280)"
      >
        {formatValue(sum, { unit, decimals })}
      </text>
    );
  };
}

/**
 * Y-Achsen-Kategorie-Tick (horizontaler Modus). Rendert das Label IMMER auf
 * einer Zeile. Der Recharts-Default-Tick (`<Text width>`) misst die Wortbreiten
 * gegen die Achsenbreite und bricht lange Labels in mehrere `<tspan>` um — das
 * Ergebnis haengt von Font-Mess-Timing ab und divergiert zwischen Astro-Dev und
 * der TYPO3-Seite. Ein eigener Tick ohne `width`-Attribut bricht nie um.
 */
function SingleLineYTick({ x = 0, y = 0, payload }: CategoryTickProps) {
  return (
    <text
      x={x}
      y={y}
      dy="0.355em"
      textAnchor="end"
      fontSize={12}
      fill="var(--color-brand-muted, #6b7280)"
    >
      {String(payload?.value ?? '')}
    </text>
  );
}

/**
 * Erzwingt gleichmäßige Schritte auf der Wertachse. Recharts wählt sonst
 * "schöne" Ticks (z.B. 0, 7, 14, 21); mit explizitem `ticks`-Array plus passender
 * Domain bestimmt der Editor die Schrittweite selbst (z.B. 5 → 0, 5, 10, …).
 * Liefert `null`, wenn keine Schrittweite gesetzt ist — dann bleibt die
 * Recharts-Automatik aktiv.
 *
 * Die Domain-Obergrenze wird auf das nächste Vielfache der Schrittweite
 * aufgerundet, damit der letzte Tick den Maximalwert einschließt. Bei
 * gestapelten Balken zählt die Stapel-Summe je Kategorie, sonst der größte
 * Einzelwert. Eine gesetzte `valueDomain` hat Vorrang und wird exakt durchschritten.
 */
function forcedValueTicks(opts: {
  data: Record<string, unknown>[];
  dataKeys: string[];
  step?: number;
  stacked: boolean;
  valueDomain?: [number, number];
}): { domain: [number, number]; ticks: number[] } | null {
  const { data, dataKeys, step, stacked, valueDomain } = opts;
  if (!step || step <= 0) return null;

  const lower = valueDomain ? valueDomain[0] : 0;
  let upper: number;
  if (valueDomain) {
    upper = valueDomain[1];
  } else {
    let max = 0;
    for (const row of data) {
      if (stacked) {
        let sum = 0;
        for (const k of dataKeys) {
          const n = Number(row[k]);
          if (Number.isFinite(n)) sum += n;
        }
        if (sum > max) max = sum;
      } else {
        for (const k of dataKeys) {
          const n = Number(row[k]);
          if (Number.isFinite(n) && n > max) max = n;
        }
      }
    }
    upper = Math.ceil(max / step) * step;
    if (upper <= lower) upper = lower + step;
  }

  const ticks: number[] = [];
  // Float-Toleranz gegen Drift (z.B. nicht-ganzzahlige Schritte) und Endlosschutz.
  for (let t = lower; t <= upper + step * 1e-9 && ticks.length < 1000; t += step) {
    ticks.push(Number(t.toFixed(6)));
  }
  return { domain: [lower, upper], ticks };
}

interface BarChartProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  xAxisKey: string;
  stacked?: boolean;
  /** Nur bei `stacked`: kehrt die Stapel-Reihenfolge um (erste Serie oben).
   *  Default true = bisheriges Verhalten; false zeigt die Editor-Reihenfolge. */
  reverseStack?: boolean;
  horizontal?: boolean;
  /** Nur bei `stacked` (vertikal): Summe aller Serien über jedem Balken. */
  showTotal?: boolean;
  /** Hängt die Einheit an die Summen-Beschriftung über den Balken an. Default false = nur Zahl. */
  showTotalUnit?: boolean;
  height?: number;
  unit?: string;
  /** Achsentitel/Einheit der X-Achse (z.B. "Quartal", "Jahr"). */
  xUnit?: string;
  /** Achsentitel/Einheit der Y-Achse (z.B. "%", "Mio. Zugkilometer"). */
  yUnit?: string;
  className?: string;
  barSize?: number;
  /** Maximale Balkenbreite in px. Verhindert unschoen breite Balken bei wenigen
   *  Datenpunkten; Recharts zentriert die Balken dann in ihrer Kategorie. */
  maxBarSize?: number;
  radius?: number;
  /** Breite der Kategorie-Achse im horizontalen Modus (px). Default 80. */
  yAxisWidth?: number;
  /** Feste Domain der Wertachse, z.B. [0, 100] fuer Prozent-Charts. */
  valueDomain?: [number, number];
  /** Farbe pro Balken (Single-Series, horizontal) — Index passend zu data. */
  barColors?: string[];
  ariaLabel?: string;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
  /** Erzwungene Schrittweite der Wertachse (X bei horizontal, sonst Y).
   *  z.B. 5 → 0, 5, 10 …  `undefined`/0 = Recharts-Automatik. */
  valueTickStep?: number;
}

/**
 * BarChart - Balkendiagramm (vertikal/horizontal, einfach/gestapelt/gruppiert).
 *
 * @example Einfach:
 * data={[{ monat: 'Jan', umsatz: 14200 }]}
 * config={{ umsatz: { label: 'Umsatz', color: '...' } }}
 *
 * @example Gestapelt (genestete Daten):
 * data={[{ monat: 'Jan', strom: 8000, gas: 4000, waerme: 2200 }]}
 * config={{ strom: {...}, gas: {...}, waerme: {...} }}
 * stacked={true}
 *
 * @example Gruppiert (genestete Daten ohne stacked):
 * data={[{ monat: 'Jan', 2024: 14200, 2025: 14900 }]}
 * config={{ 2024: {...}, 2025: {...} }}
 */
export function CPBarChart({
  data,
  config,
  xAxisKey,
  stacked = false,
  reverseStack = true,
  horizontal = false,
  showTotal = false,
  showTotalUnit = false,
  height = 350,
  unit,
  xUnit,
  yUnit,
  className,
  barSize,
  maxBarSize = 64,
  radius = 0,
  yAxisWidth = 80,
  valueDomain,
  barColors,
  ariaLabel,
  decimals,
  valueTickStep,
}: BarChartProps) {
  const dataKeys = Object.keys(config);
  // Recharts baut die Legende aus der Kinder-Reihenfolge der <Bar> auf. Wir
  // rendern die Serien daher IMMER in Config-Reihenfolge, damit die Legende
  // konsistent bleibt (z.B. "gut → schlecht"). Die visuelle Stapel-Reihenfolge
  // steuert separat `reverseStackOrder` am Chart: per Default liegt so die erste
  // Serie oben, ohne die Legende mitzudrehen. Via `reverseStack={false}`
  // (Backend-Checkbox) bleibt die Editor-Reihenfolge erhalten (erste Serie unten).
  const reverseStackVisual = stacked && reverseStack;
  // Wertachsen-Titel: explizites yUnit hat Vorrang, sonst die Tooltip-Einheit.
  const valueLabel = yUnit ?? unit;
  // Stapel-Summe nur bei vertikal gestapelten Balken sinnvoll.
  const showStackTotal = stacked && showTotal && !horizontal;
  // Visuell oberster Balken trägt die Summen-Beschriftung: bei umgekehrter
  // Stapelung (reverseStackVisual) ist das die erste Config-Serie, sonst die letzte.
  const topBarKey = reverseStackVisual ? dataKeys[0] : dataKeys[dataKeys.length - 1];
  const renderStackTotal = makeStackTotalLabel(data, dataKeys, showTotalUnit ? unit : undefined, decimals);
  // Etwas mehr Luft oben, damit die Summe nicht am Diagrammrand klebt.
  const margin = axisMargin({ xUnit, yUnit: valueLabel });
  if (showStackTotal) {
    margin.top = Math.max(margin.top, 22);
  }
  // Erzwungene, gleichmäßige Ticks auf der Wertachse (opt-in via valueTickStep).
  // `interval={0}` zwingt Recharts, jeden vorgegebenen Tick zu zeigen.
  const forced = forcedValueTicks({ data, dataKeys, step: valueTickStep, stacked, valueDomain });
  const valueAxisDomain = forced ? forced.domain : valueDomain;
  const valueAxisTicks = forced ? forced.ticks : undefined;
  const buildChart = (containerWidth: number) => {
    // Kategorie-Achse: auf schmalen Viewports werden die Labels gedreht, sonst
    // zweizeilig umgebrochen (geteilte Logik, gilt für alle Chart-Typen).
    const { angled, height: categoryAxisHeight } = categoryAxis({
      data,
      xAxisKey,
      containerWidth,
      horizontal,
      xUnit,
    });

    return (
      <RechartsBarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={margin}
        reverseStackOrder={reverseStackVisual}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--color-brand-border, #e5e7eb)"
          horizontal={!horizontal}
          vertical={horizontal}
        />
        {horizontal ? (
          <>
            <XAxis type="number" domain={valueAxisDomain} ticks={valueAxisTicks} interval={valueAxisTicks ? 0 : undefined} tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }} tickLine={false} axisLine={false} label={axisLabel(valueLabel, 'x')} />
            <YAxis dataKey={xAxisKey} type="category" tick={<SingleLineYTick />} tickLine={false} axisLine={false} width={yAxisWidth} label={axisLabel(xUnit, 'y')} />
          </>
        ) : (
          <>
            <XAxis dataKey={xAxisKey} interval={0} tick={<CategoryTick angled={angled} />} height={categoryAxisHeight} tickLine={false} axisLine={false} label={axisLabel(xUnit, 'x')} />
            <YAxis domain={valueAxisDomain} ticks={valueAxisTicks} interval={valueAxisTicks ? 0 : undefined} tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }} tickLine={false} axisLine={false} label={axisLabel(valueLabel, 'y')} />
          </>
        )}
        <Tooltip content={<ChartTooltipContent config={config} unit={unit} decimals={decimals} />} />
        {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
            nach series_key ('value'); null behält die Config-/Editor-Reihenfolge. */}
        <Legend
          itemSorter={null}
          iconType="circle"
          iconSize={8}
          wrapperStyle={legendStyle(xUnit)}
          formatter={(value: string) => config[value]?.label ?? value}
        />
        {dataKeys.map((key) => {
          // Der oberste Balken trägt die Summen-Beschriftung. Recharts filtert
          // 0-Höhen-Rechtecke (Wert 0) raus — dann fehlt für diese Reihen der
          // LabelList-Eintrag UND die übrigen Labels verrutschen im Index, weil
          // LabelList über das *gefilterte* Rect-Array iteriert. Eine eigene
          // `shape` setzt Recharts' `hasCustomShape`-Flag → 0-Höhen-Segmente
          // bleiben erhalten (unsichtbar gerendert), Labels bleiben dicht und
          // index-stabil. <Rectangle/> entspricht dem Default-Balken.
          const carriesTotal = showStackTotal && key === topBarKey;
          return (
            <Bar
              key={key}
              dataKey={key}
              stackId={stacked ? 'stack' : undefined}
              fill={`var(--color-${key})`}
              radius={[radius, radius, 0, 0] as [number, number, number, number]}
              barSize={barSize}
              maxBarSize={maxBarSize}
              shape={carriesTotal ? <Rectangle /> : undefined}
            >
              {barColors
                ? data.map((_, i) => (
                    <Cell key={i} fill={barColors[i] ?? `var(--color-${key})`} />
                  ))
                : null}
              {carriesTotal ? (
                <LabelList dataKey={key} content={renderStackTotal} />
              ) : null}
            </Bar>
          );
        })}
      </RechartsBarChart>
    );
  };

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      {(containerWidth) => buildChart(containerWidth)}
    </ChartContainer>
  );
}
